import { RubyGemMark } from '../../components/RubyGemMark';
/**
 * Apps tab — one row per app: current version per environment/target, drift
 * count (a ring pointing at something other than the newest registered
 * build), and the targeting model (ring-based; see release-service Phase A).
 *
 * `agenticpos-dynamodb-releases-dev` holds ZERO rows today (verified by the
 * D2 controller: `scan --select COUNT` returned 0 — CI has never published a
 * build, Phase B's verification rows were deleted). So `apps: []` is the
 * REAL first-render state for anyone opening this hub in dev, not an edge
 * case — it gets its own designed empty state below, never a bare table or
 * a spinner that never resolves.
 *
 * PROMOTION LIVES HERE. Registering a build is NOT promoting it: until a
 * `ring#` row exists, a paired, online, healthy till check-in is answered with
 * no `target` at all — it is authoritatively told "nothing to install",
 * forever. `POST /rings` (release-service `promote.ts`) is the only call that
 * writes that row, and nothing in this UI called it before. Note that the
 * "Promote to fleet" button on the POS branding page is a DIFFERENT pipeline
 * (device-config branding manifests) that shares the words — it never moves an
 * app version.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, PackageOpen, RefreshCw, Rocket, X } from 'lucide-react';
import { buildShortId, RELEASE_RINGS, type BuildRecord, type ReleaseEnv, type ReleaseRing } from '@platform/shared-types';
import { releaseApi, currentReleaseEnv } from '../../api/releaseApi';
import { deviceConfigApi } from '../../services/deviceConfigApi';
import { summariseApps, type AppSummaryRow } from '../../api/releaseSummary';
import { RubyLoader } from '../../components/RubyLoader';

const RING_ORDER = ['canary', 'pilot', 'stable'] as const;

/** What promoting to each ring actually reaches. Sparse on purpose. */
const RING_REACH: Record<ReleaseRing, string> = {
  canary: 'the few tills put on canary',
  pilot: 'the pilot tills',
  stable: 'every till — stable is the default ring, so a device nobody ever moved is on it',
};

interface PromoteCtx {
  appId: string;
  env: ReleaseEnv;
  target: string;
  newestBuild?: BuildRecord;
}

/** device-config rows for one deviceType; `target` IS the deviceType (consumePromotion.ts writes `deviceType: m.target`). */
interface FleetCount {
  /** Devices in this ring with auto-update on, or null when it could not be established. */
  count: number | null;
  /** True when the server said its read was cut short — the real number is higher. */
  atLeast: boolean;
}

const RingChip: React.FC<{ ringName: string; version: string; resolved: boolean; drifting: boolean }> = ({ ringName, version, resolved, drifting }) => {
  const tone = !resolved
    ? 'bg-amber-100 text-amber-800 border-amber-300'
    : drifting
      ? 'bg-orange-100 text-orange-800 border-orange-300'
      : 'bg-emerald-100 text-emerald-800 border-emerald-300';
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-medium ${tone}`} title={!resolved ? 'This ring points at a version with no matching build record' : undefined}>
      <span className="capitalize">{ringName}</span>
      <span className="font-mono">{version}</span>
      {!resolved && <span>unresolved</span>}
    </span>
  );
};

/**
 * Two-step promote. Step 1 picks the build and the ring; step 2 states the
 * blast radius and is the ONLY place `releaseApi.rings.promote` is called —
 * choosing a ring must never be able to move a device on its own.
 *
 * No `window.confirm`: banned platform-wide, and a browser chrome dialog
 * cannot name a device count anyway.
 */
const PromoteDialog: React.FC<{ ctx: PromoteCtx; onClose: () => void; onPromoted: () => void }> = ({ ctx, onClose, onPromoted }) => {
  const [step, setStep] = useState<'choose' | 'confirm'>('choose');
  const [builds, setBuilds] = useState<BuildRecord[] | null>(null);
  const [version, setVersion] = useState<string>(ctx.newestBuild?.version || '');
  const [ring, setRing] = useState<ReleaseRing>('canary');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fleet, setFleet] = useState<Array<{ releaseRing: string; autoUpdate: boolean }> | null>(null);
  const [fleetTruncated, setFleetTruncated] = useState(false);

  // Builds for this app/env, so a ring can be pointed at something older than
  // the newest — the handler only accepts a version it actually registered.
  useEffect(() => {
    let live = true;
    releaseApi.builds.list(ctx.appId, ctx.env)
      .then((res) => {
        if (!live) return;
        const mine = (res.items || []).filter((b) => b.target === ctx.target);
        setBuilds(mine);
        if (!ctx.newestBuild?.version && mine[0]) setVersion(mine[0].version);
      })
      .catch(() => { if (live) setBuilds([]); });
    return () => { live = false; };
  }, [ctx.appId, ctx.env, ctx.target, ctx.newestBuild?.version]);

  // Blast radius. device-config-service is STAGE-scoped, so its numbers only
  // describe this env when the browser is pointed at the matching stage —
  // otherwise no count is claimed at all.
  useEffect(() => {
    if (currentReleaseEnv() !== ctx.env) return;
    let live = true;
    deviceConfigApi.listDeviceStatus(ctx.target)
      .then((res) => {
        if (!live) return;
        setFleetTruncated(Boolean(res?.truncated));
        setFleet((res?.devices || []).map((d: any) => ({
          releaseRing: String(d?.releaseRing || 'stable'),
          // device-config's own default: only an explicit false is off.
          autoUpdate: d?.autoUpdate !== false,
        })));
      })
      .catch(() => { if (live) setFleet(null); });
    return () => { live = false; };
  }, [ctx.target, ctx.env]);

  const reach: FleetCount = useMemo(() => {
    if (!fleet) return { count: null, atLeast: false };
    return {
      count: fleet.filter((d) => d.releaseRing === ring && d.autoUpdate).length,
      atLeast: fleetTruncated,
    };
  }, [fleet, ring, fleetTruncated]);

  const options = builds && builds.length > 0 ? builds : ctx.newestBuild ? [ctx.newestBuild] : [];
  const chosen = options.find((b) => b.version === version);

  const promote = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await releaseApi.rings.promote({ appId: ctx.appId, env: ctx.env, target: ctx.target, ring, version });
      onPromoted();
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Promote failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 p-4" role="dialog" aria-modal="true" aria-label="Promote to ring">
      <div className="bg-white border border-stone-200 rounded-2xl w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-stone-100">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-stone-800">Promote to ring</h2>
            <p className="text-xs text-stone-500 truncate">{ctx.appId} · {ctx.target} · {ctx.env}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100">
            <X size={18} />
          </button>
        </div>

        {step === 'choose' ? (
          <div className="px-5 py-4 space-y-4">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-stone-400 mb-2">Build</div>
              {options.length === 0 ? (
                <p className="text-xs text-stone-500">No registered build for this target — nothing can be promoted yet.</p>
              ) : (
                <div className="space-y-1.5 max-h-56 overflow-y-auto">
                  {options.map((b) => (
                    <button
                      key={b.version}
                      onClick={() => setVersion(b.version)}
                      aria-pressed={b.version === version}
                      className={`w-full text-left px-3 py-2 min-h-[44px] rounded-xl border text-xs ${b.version === version ? 'border-[#DC143C] bg-rose-50 text-stone-800' : 'border-stone-200 hover:bg-stone-50 text-stone-600'}`}
                    >
                      <span className="font-mono font-semibold">{buildShortId(b)}</span>
                      {b.label && <span> · {b.label}</span>}
                      <span className="font-mono"> · {b.version}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-stone-400 mb-2">Ring</div>
              <div className="flex gap-1.5 flex-wrap">
                {RELEASE_RINGS.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRing(r)}
                    aria-pressed={r === ring}
                    className={`px-4 py-2 min-h-[44px] rounded-xl border text-xs font-medium capitalize ${r === ring ? 'border-[#DC143C] bg-[#DC143C] text-white' : 'border-stone-200 text-stone-600 hover:bg-stone-50'}`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <button onClick={onClose} className="px-4 py-2 min-h-[44px] rounded-xl border border-stone-200 text-sm text-stone-600 hover:bg-stone-50">Cancel</button>
              <button
                onClick={() => setStep('confirm')}
                disabled={!chosen}
                className="px-4 py-2 min-h-[44px] rounded-xl bg-stone-800 text-white text-sm font-medium hover:bg-stone-700 disabled:opacity-40"
              >
                Review
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-4 space-y-4">
            <div className="rounded-xl border border-[#DC143C] bg-rose-50 p-4 space-y-2">
              <p className="text-sm font-semibold text-stone-800">
                Promote {chosen ? buildShortId(chosen) : version} to <span className="capitalize">{ring}</span> on {ctx.target}?
              </p>
              <p className="text-xs text-stone-700">
                {reach.count === null
                  ? `Device count unavailable. Assume ${RING_REACH[ring]}.`
                  : `${reach.atLeast ? 'At least ' : ''}${reach.count} device${reach.count === 1 ? '' : 's'} on ${ctx.target} ${reach.count === 1 ? 'is' : 'are'} in ${ring} with auto-update on — ${RING_REACH[ring]}.`}
              </p>
              <p className="text-xs text-stone-700">
                They start downloading this bundle within about 3 minutes. There is no undo other than promoting a different build.
              </p>
              <p className="text-[11px] font-mono text-stone-500">{ctx.appId} · {ctx.env} · {version}</p>
            </div>

            {error && (
              <p role="alert" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                Not promoted — {error}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={() => setStep('choose')} disabled={submitting} className="px-4 py-2 min-h-[44px] rounded-xl border border-stone-200 text-sm text-stone-600 hover:bg-stone-50 disabled:opacity-40">Back</button>
              <button
                onClick={promote}
                disabled={submitting}
                className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] rounded-xl bg-[#DC143C] text-white text-sm font-semibold hover:bg-[#b91036] disabled:opacity-60"
              >
                {submitting ? <RubyGemMark size={14} drawing color="currentColor" /> : <Rocket size={14} />}
                Promote to {ring}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AppRow: React.FC<{ row: AppSummaryRow; onPromote: (ctx: PromoteCtx) => void }> = ({ row, onPromote }) => (
  <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
    <div className="px-4 py-3 flex items-center justify-between gap-3 border-b border-stone-100 bg-stone-50">
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-semibold text-stone-800 truncate">{row.appId}</span>
        <span className="text-[11px] text-stone-400">Ring-based targeting (canary → pilot → stable)</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!row.hasAnyBuilds ? (
          <span className="px-2 py-1 rounded-md bg-stone-200 text-stone-600 text-[11px] font-medium">No builds yet</span>
        ) : row.totalDrift > 0 ? (
          <span className="px-2 py-1 rounded-md bg-orange-100 text-orange-800 border border-orange-300 text-[11px] font-medium">
            {row.totalDrift} ring{row.totalDrift === 1 ? '' : 's'} drifting
          </span>
        ) : (
          <span className="px-2 py-1 rounded-md bg-emerald-100 text-emerald-800 border border-emerald-300 text-[11px] font-medium">Up to date</span>
        )}
      </div>
    </div>

    <div className="divide-y divide-stone-100">
      {row.environments.map((env) => (
        <div key={env.env} className="px-4 py-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-stone-400 mb-2">{env.env}</div>
          <div className="space-y-2">
            {env.targets.map((t) => (
              <div key={t.target} className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm text-stone-700 font-medium">{t.target}</div>
                  {t.newestBuild ? (
                    <div className="text-xs text-stone-500">
                      {/* Readable id first, then the operator's label, then the
                          exact version stamp — the same order the Builds tab
                          uses, so the two tabs never name a build differently. */}
                      <span className="font-mono font-semibold text-stone-700">newest {buildShortId(t.newestBuild)}</span>
                      {t.newestBuild.label && <span className="text-stone-600"> · {t.newestBuild.label}</span>}
                      <span className="font-mono"> · {t.newestBuild.version}</span>
                      <span> · built {new Date(t.newestBuild.builtAt).toLocaleString()}</span>
                    </div>
                  ) : (
                    <div className="text-xs text-stone-400 italic">no builds registered for this target/environment yet</div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {RING_ORDER.filter((r) => t.rings.some((ring) => ring.ring === r)).map((r) => {
                    const ring = t.rings.find((x) => x.ring === r)!;
                    const drifting = t.driftingRings.some((d) => d.ring === r);
                    return <RingChip key={r} ringName={ring.ring} version={ring.version} resolved={ring.resolved} drifting={drifting} />;
                  })}
                  {/* NOT "no rings promoted yet" — that reads like a pending
                      step. With no ring row, check-in returns no target and
                      the device is told there is nothing to install, forever.
                      Say that, and offer the fix. */}
                  {t.rings.length === 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-rose-50 border border-[#DC143C] text-[11px] font-medium text-[#DC143C]">
                      <AlertTriangle size={12} />
                      No ring — no device on this target will ever update
                    </span>
                  )}
                  {t.newestBuild ? (
                    <button
                      onClick={() => onPromote({ appId: row.appId, env: env.env, target: t.target, newestBuild: t.newestBuild })}
                      className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] rounded-xl border border-[#DC143C] text-[#DC143C] text-xs font-semibold hover:bg-rose-50"
                    >
                      <Rocket size={13} /> Promote to ring
                    </button>
                  ) : (
                    t.rings.length === 0 && <span className="text-[11px] text-stone-400 italic">nothing registered to promote yet</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {row.environments.length === 0 && (
        <div className="px-4 py-3 text-xs text-stone-400 italic">no environment data recorded for this app yet</div>
      )}
    </div>
  </div>
);

const AppsTab: React.FC = () => {
  const [rows, setRows] = useState<AppSummaryRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [promoteFor, setPromoteFor] = useState<PromoteCtx | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await releaseApi.apps.list();
      setRows(summariseApps(res.apps || []));
    } catch (e: any) {
      setError(e?.message || 'Failed to load apps');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && rows === null) {
    return (
      <RubyLoader variant="page" label="Loading apps…" />
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded-xl p-8 text-center">
        <AlertTriangle size={28} className="mx-auto text-red-500 mb-2" />
        <p className="text-sm text-stone-700 font-medium mb-1">Could not load apps</p>
        <p className="text-xs text-stone-500 mb-4">{error}</p>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] rounded-lg bg-stone-800 text-white text-sm font-medium hover:bg-stone-700"
        >
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="bg-white border border-stone-200 rounded-xl p-10 text-center">
        <PackageOpen size={32} className="mx-auto text-stone-300 mb-3" />
        <p className="text-sm font-semibold text-stone-700 mb-1">No apps registered yet</p>
        <p className="text-xs text-stone-500 max-w-md mx-auto">
          Nothing has been published to release-service in this environment. This
          is expected for a brand-new hub — apps appear here automatically the
          moment CI registers its first build (<code className="font-mono">POST /builds</code>).
          There is nothing to configure on this page; check back after the next build.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((row) => <AppRow key={row.appId} row={row} onPromote={setPromoteFor} />)}
      {promoteFor && (
        <PromoteDialog ctx={promoteFor} onClose={() => setPromoteFor(null)} onPromoted={load} />
      )}
    </div>
  );
};

export default AppsTab;
